import { zodResolver } from "@hookform/resolvers/zod"
import { ChevronDown } from "lucide-react"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import {
  useUpdateWebhook,
  useWebhookById,
} from "@/hooks/webhooks"
import { cn } from "@/lib/utils"

import {
  ANTIFRAUD_EVENTS,
  CARD_EVENTS,
  CONCILIATION_EVENTS,
  EVENT_LABELS,
  GENERAL_EVENTS,
  ORDER_EVENTS,
  ORDER_INSURANCE_EVENTS,
  PAYMENT_EVENTS,
  SUBSCRIPTION_CYCLE_EVENTS,
  SUBSCRIPTION_EVENTS,
  SUBSCRIPTION_INSURANCE_CYCLE_EVENTS,
  SUBSCRIPTION_INSURANCE_EVENTS,
  type WebhookEvent,
} from "./events"
const EVENT_GROUPS = [
  { id: "general", label: "Geral", events: GENERAL_EVENTS },
  { id: "orders", label: "Pedidos", events: ORDER_EVENTS },
  {
    id: "subscriptions",
    label: "Assinaturas",
    events: SUBSCRIPTION_EVENTS,
  },
  {
    id: "subscription_cycles",
    label: "Ciclos de Assinatura",
    events: SUBSCRIPTION_CYCLE_EVENTS,
  },
  {
    id: "order_insurance",
    label: "Pedidos de Seguro",
    events: ORDER_INSURANCE_EVENTS,
  },
  {
    id: "subscription_insurance",
    label: "Assinatura de Seguro",
    events: SUBSCRIPTION_INSURANCE_EVENTS,
  },
  {
    id: "subscription_insurance_cycles",
    label: "Ciclos de Cobrança de Seguro",
    events: SUBSCRIPTION_INSURANCE_CYCLE_EVENTS,
  },
  { id: "cards", label: "Cartões", events: CARD_EVENTS },
  {
    id: "antifraud",
    label: "Antifraude",
    events: ANTIFRAUD_EVENTS,
  },
  {
    id: "payments",
    label: "Pagamentos",
    events: PAYMENT_EVENTS,
  },
  {
    id: "conciliation",
    label: "Conciliação",
    events: CONCILIATION_EVENTS,
  },
] as const
const webhookFormSchema = z.object({
  description: z.string().min(1, "Descrição é obrigatória"),
  url: z.string().url("URL inválida"),
  events: z
    .array(z.string())
    .min(1, "Selecione pelo menos um evento"),
  maxRetry: z.coerce.number().min(0).max(10),
  blockManyErrors: z.boolean(),
  secretKey: z.string().optional(),
  active: z.boolean(),
})
type WebhookFormValues = z.infer<typeof webhookFormSchema>
interface WebhookEditFormProps {
  webhookId: string
}
export const WebhookEditForm = ({
  webhookId,
}: WebhookEditFormProps) => {
  const navigate = useNavigate()
  const [openGroups, setOpenGroups] = useState<string[]>([
    "general",
  ])
  const { data: webhook, isLoading } =
    useWebhookById(webhookId)
  const updateMutation = useUpdateWebhook()
  const form = useForm<WebhookFormValues>({
    resolver: zodResolver(webhookFormSchema),
    defaultValues: {
      description: "",
      url: "",
      events: [],
      maxRetry: 3,
      blockManyErrors: false,
      secretKey: "",
      active: true,
    },
  })
  useEffect(() => {
    if (webhook) {
      form.reset({
        description: webhook.description,
        url: webhook.url,
        events: webhook.events,
        maxRetry: webhook.maxRetry,
        blockManyErrors: webhook.blockManyErrors,
        secretKey: webhook.secretKey || "",
        active: webhook.active,
      })
    }
  }, [webhook, form])
  const toggleGroup = (groupId: string) => {
    setOpenGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    )
  }
  const onSubmit = async (values: WebhookFormValues) => {
    updateMutation.mutate(
      {
        webhookId,
        data: values,
      },
      {
        onSuccess: () => {
          navigate(`/webhooks/details/${webhookId}`)
        },
      }
    )
  }
  // eslint-disable-next-line react-hooks/incompatible-library
  const selectedEvents = form.watch("events")
  const toggleEvent = (event: string, checked: boolean) => {
    const current = form.getValues("events")
    if (checked) {
      form.setValue("events", [...current, event])
    } else {
      form.setValue(
        "events",
        current.filter((e) => e !== event)
      )
    }
  }
  const getGroupSelectedCount = (
    events: readonly string[]
  ) => {
    return events.filter((e) => selectedEvents.includes(e))
      .length
  }
  const toggleGroupAll = (
    events: readonly string[],
    checked: boolean
  ) => {
    const current = form.getValues("events")
    if (checked) {
      const newEvents = [
        ...current,
        ...events.filter((e) => !current.includes(e)),
      ]
      form.setValue("events", newEvents)
    } else {
      form.setValue(
        "events",
        current.filter((e) => !events.includes(e))
      )
    }
  }
  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p>Carregando...</p>
        </CardContent>
      </Card>
    )
  }
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-6"
      >
        <Card>
          <CardHeader>
            <CardTitle>Informações Básicas</CardTitle>
            <p className="text-sm text-muted-foreground">
              Configure as informações principais do webhook
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Ex: Webhook de notificações"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>URL</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="secretKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Chave Secreta (opcional)
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Chave para validação"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Chave usada para assinar as requisições
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="maxRetry"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Máximo de Tentativas
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        max={10}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="blockManyErrors"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Bloquear em Erros
                      </FormLabel>
                      <FormDescription>
                        Desativar após muitos erros
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Ativo
                    </FormLabel>
                    <FormDescription>
                      Habilitar ou desabilitar o webhook
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Eventos</CardTitle>
            <p className="text-sm text-muted-foreground">
              Selecione os eventos que dispararão o webhook
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            <FormField
              control={form.control}
              name="events"
              render={() => (
                <FormItem>
                  {EVENT_GROUPS.map((group) => {
                    const selectedCount =
                      getGroupSelectedCount(group.events)
                    const isOpen = openGroups.includes(
                      group.id
                    )
                    const allSelected =
                      selectedCount === group.events.length
                    return (
                      <Collapsible
                        key={group.id}
                        open={isOpen}
                        onOpenChange={() =>
                          toggleGroup(group.id)
                        }
                        className="rounded-lg border"
                      >
                        <CollapsibleTrigger asChild>
                          <div className="flex cursor-pointer items-center justify-between p-4 hover:bg-muted/50">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={allSelected}
                                onCheckedChange={(
                                  checked
                                ) => {
                                  toggleGroupAll(
                                    group.events,
                                    checked as boolean
                                  )
                                }}
                                onClick={(e) =>
                                  e.stopPropagation()
                                }
                              />
                              <span className="font-medium">
                                {group.label}
                              </span>
                              <span className="text-sm text-muted-foreground">
                                ({selectedCount}/
                                {group.events.length})
                              </span>
                            </div>
                            <ChevronDown
                              className={cn(
                                "h-4 w-4 transition-transform",
                                isOpen && "rotate-180"
                              )}
                            />
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="grid grid-cols-2 gap-2 border-t p-4">
                            {group.events.map((event) => (
                              <div
                                key={event}
                                className="flex items-center gap-2"
                              >
                                <Checkbox
                                  id={event}
                                  checked={selectedEvents.includes(
                                    event
                                  )}
                                  onCheckedChange={(
                                    checked
                                  ) =>
                                    toggleEvent(
                                      event,
                                      checked as boolean
                                    )
                                  }
                                />
                                <label
                                  htmlFor={event}
                                  className="cursor-pointer text-sm"
                                >
                                  {
                                    EVENT_LABELS[
                                      event as WebhookEvent
                                    ]
                                  }
                                </label>
                              </div>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )
                  })}
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending
              ? "Salvando..."
              : "Salvar"}
          </Button>
        </div>
      </form>
    </Form>
  )
}
